import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { equipeService, CreateEquipeDTO, AgentWithEquipe } from '@/services/equipeService';
import type { EquipeDeneignement } from '@/types';
import toast from 'react-hot-toast';
import { getErrorMessage } from '@/services/api';
import { ConfirmModal } from '@/components/common/Modal';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  UserPlusIcon,
  UserMinusIcon,
  MagnifyingGlassIcon,
  ArrowsRightLeftIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';

/**
 * Page de gestion des equipes de deneigement
 */
export function EquipesPage() {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingEquipe, setEditingEquipe] = useState<EquipeDeneignement | null>(null);
  const [showAgentModal, setShowAgentModal] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<EquipeDeneignement | null>(null);
  const [removeAgentTarget, setRemoveAgentTarget] = useState<{ equipeId: number; agent: { id: number; prenom: string; nom: string } } | null>(null);

  // Chargement des equipes
  const { data: equipes, isLoading } = useQuery({
    queryKey: ['equipes'],
    queryFn: () => equipeService.list(),
  });

  // Tous les agents de deneignement
  const { data: allAgents } = useQuery({
    queryKey: ['all-agents'],
    queryFn: () => equipeService.getAllAgents(),
  });

  // Filtre de recherche pour le modal
  const [agentSearch, setAgentSearch] = useState('');
  const [showOnlyAvailable, setShowOnlyAvailable] = useState(false);

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: CreateEquipeDTO) => equipeService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipes'] });
      setShowModal(false);
      toast.success('Equipe creee avec succes');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CreateEquipeDTO> }) =>
      equipeService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipes'] });
      setShowModal(false);
      setEditingEquipe(null);
      toast.success('Equipe modifiee avec succes');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => equipeService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipes'] });
      toast.success('Equipe supprimee');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const addAgentMutation = useMutation({
    mutationFn: ({ equipeId, agentId }: { equipeId: number; agentId: number }) =>
      equipeService.addAgent(equipeId, agentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipes'] });
      queryClient.invalidateQueries({ queryKey: ['all-agents'] });
      toast.success('Agent affecte a l\'equipe');
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || 'Erreur lors de l\'affectation';
      toast.error(message);
    },
  });

  const removeAgentMutation = useMutation({
    mutationFn: ({ equipeId, agentId }: { equipeId: number; agentId: number }) =>
      equipeService.removeAgent(equipeId, agentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipes'] });
      queryClient.invalidateQueries({ queryKey: ['all-agents'] });
      toast.success('Agent retire de l\'equipe');
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || 'Erreur lors du retrait';
      toast.error(message);
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const data: CreateEquipeDTO = {
      nom: formData.get('nom') as string,
      code: formData.get('code') as string,
    };

    if (editingEquipe) {
      updateMutation.mutate({ id: editingEquipe.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDelete = (equipe: EquipeDeneignement) => {
    setDeleteTarget(equipe);
  };

  const confirmDelete = () => {
    if (deleteTarget) {
      deleteMutation.mutate(deleteTarget.id);
      setDeleteTarget(null);
    }
  };

  const handleRemoveAgent = (equipeId: number, agent: { id: number; prenom: string; nom: string }) => {
    setRemoveAgentTarget({ equipeId, agent });
  };

  const confirmRemoveAgent = () => {
    if (removeAgentTarget) {
      removeAgentMutation.mutate({
        equipeId: removeAgentTarget.equipeId,
        agentId: removeAgentTarget.agent.id,
      });
      setRemoveAgentTarget(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
        <p className="text-sm text-gray-500 mt-3">Chargement des equipes...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Gestion des equipes</h2>
        <button
          onClick={() => {
            setEditingEquipe(null);
            setShowModal(true);
          }}
          className="btn-primary"
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          Nouvelle equipe
        </button>
      </div>

      {/* Liste des equipes */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {equipes?.map((equipe) => (
          <div key={equipe.id} className="card">
            <div className="card-body">
              {/* Header equipe */}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{equipe.nom}</h3>
                  <p className="text-sm text-gray-500">Code: {equipe.code}</p>
                </div>
                <span
                  className={`px-2 py-1 text-xs rounded-full ${
                    equipe.actif
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {equipe.actif ? 'Active' : 'Inactive'}
                </span>
              </div>

              {/* Chef */}
              {equipe.chef && (
                <div className="mb-4">
                  <p className="text-xs text-gray-500 uppercase mb-1">Chef d'equipe</p>
                  <p className="text-sm font-medium">
                    {equipe.chef.prenom} {equipe.chef.nom}
                  </p>
                </div>
              )}

              {/* Agents */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-gray-500 uppercase">
                    Agents ({equipe.agents?.length || 0})
                  </p>
                  <button
                    onClick={() => setShowAgentModal(equipe.id)}
                    className="text-primary-500 hover:text-primary-600"
                    title="Ajouter un agent"
                  >
                    <UserPlusIcon className="w-4 h-4" />
                  </button>
                </div>
                <div className="space-y-1">
                  {equipe.agents?.slice(0, 3).map((agent) => (
                    <div
                      key={agent.id}
                      className="flex items-center justify-between text-sm bg-gray-50 px-2 py-1 rounded"
                    >
                      <span>
                        {agent.prenom} {agent.nom}
                      </span>
                      <button
                        onClick={() => handleRemoveAgent(equipe.id, agent)}
                        className="text-red-400 hover:text-red-600"
                        title="Retirer"
                      >
                        <UserMinusIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  {equipe.agents?.length > 3 && (
                    <p className="text-xs text-gray-400">
                      +{equipe.agents.length - 3} autres
                    </p>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end space-x-2 pt-4 border-t">
                <button
                  onClick={() => {
                    setEditingEquipe(equipe);
                    setShowModal(true);
                  }}
                  className="p-2 text-gray-400 hover:text-primary-500"
                  title="Modifier"
                >
                  <PencilIcon className="w-5 h-5" />
                </button>
                <button
                  onClick={() => handleDelete(equipe)}
                  className="p-2 text-gray-400 hover:text-red-500"
                  title="Supprimer"
                >
                  <TrashIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {equipes?.length === 0 && (
        <div className="text-center py-12">
          <UserGroupIcon className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500 mb-3">Aucune equipe configuree</p>
          <button
            onClick={() => { setEditingEquipe(null); setShowModal(true); }}
            className="btn-primary inline-flex items-center"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Creer une equipe
          </button>
        </div>
      )}

      {/* Modale confirmation suppression */}
      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title={`Supprimer l'equipe "${deleteTarget?.nom}" ?`}
        message="Cette action est irreversible. L'equipe et ses affectations seront supprimees."
        confirmLabel="Supprimer"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />

      {/* Modale confirmation retrait agent */}
      <ConfirmModal
        isOpen={!!removeAgentTarget}
        onClose={() => setRemoveAgentTarget(null)}
        onConfirm={confirmRemoveAgent}
        title="Retirer l'agent de l'equipe ?"
        message={removeAgentTarget
          ? `Retirer ${removeAgentTarget.agent.prenom} ${removeAgentTarget.agent.nom} de cette equipe ?`
          : ''}
        confirmLabel="Retirer"
        variant="warning"
        isLoading={removeAgentMutation.isPending}
      />

      {/* Modal creation/edition */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {editingEquipe ? 'Modifier l\'equipe' : 'Nouvelle equipe'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="label">Nom</label>
                <input
                  type="text"
                  name="nom"
                  defaultValue={editingEquipe?.nom}
                  className="input"
                  required
                />
              </div>
              <div>
                <label className="label">Code</label>
                <input
                  type="text"
                  name="code"
                  defaultValue={editingEquipe?.code}
                  className="input"
                  required
                  maxLength={10}
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingEquipe(null);
                  }}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  {editingEquipe ? 'Modifier' : 'Creer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal ajout agent */}
      {showAgentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold mb-4">
              Affecter un agent a l'equipe
            </h3>

            {/* Filtres */}
            <div className="space-y-3 mb-4">
              <div className="relative">
                <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  className="input pl-10"
                  placeholder="Rechercher un agent..."
                  value={agentSearch}
                  onChange={(e) => setAgentSearch(e.target.value)}
                />
              </div>
              <label className="flex items-center space-x-2 text-sm">
                <input
                  type="checkbox"
                  checked={showOnlyAvailable}
                  onChange={(e) => setShowOnlyAvailable(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <span>Afficher uniquement les agents sans equipe</span>
              </label>
            </div>

            {/* Liste des agents */}
            <div className="border rounded-lg max-h-64 overflow-y-auto mb-4">
              {(() => {
                const currentEquipe = equipes?.find(e => e.id === showAgentModal);
                const filteredAgents = allAgents?.filter((agent) => {
                  // Filtre de recherche
                  const searchMatch = agentSearch === '' ||
                    agent.nomComplet.toLowerCase().includes(agentSearch.toLowerCase()) ||
                    agent.email.toLowerCase().includes(agentSearch.toLowerCase());

                  // Filtre agents disponibles uniquement
                  const availableMatch = !showOnlyAvailable || !agent.equipeActuelle;

                  // Exclure les agents deja dans cette equipe
                  const notInCurrentEquipe = !currentEquipe?.agents?.some(a => a.id === agent.id);

                  return searchMatch && availableMatch && notInCurrentEquipe;
                });

                if (!filteredAgents || filteredAgents.length === 0) {
                  return (
                    <p className="text-gray-500 text-center py-8">
                      Aucun agent trouve
                    </p>
                  );
                }

                return filteredAgents.map((agent) => (
                  <div
                    key={agent.id}
                    className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 border-b last:border-b-0"
                  >
                    <div>
                      <p className="font-medium">{agent.nomComplet}</p>
                      <p className="text-sm text-gray-500">{agent.email}</p>
                      {agent.equipeActuelle && (
                        <p className="text-xs text-orange-600">
                          Actuellement dans: {agent.equipeActuelle.nom}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        addAgentMutation.mutate({
                          equipeId: showAgentModal,
                          agentId: agent.id,
                        });
                      }}
                      disabled={addAgentMutation.isPending}
                      className={`flex items-center px-3 py-1.5 rounded text-sm ${
                        agent.equipeActuelle
                          ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                          : 'bg-primary-100 text-primary-700 hover:bg-primary-200'
                      }`}
                      title={agent.equipeActuelle ? 'Transferer' : 'Affecter'}
                    >
                      {agent.equipeActuelle ? (
                        <>
                          <ArrowsRightLeftIcon className="w-4 h-4 mr-1" />
                          Transferer
                        </>
                      ) : (
                        <>
                          <UserPlusIcon className="w-4 h-4 mr-1" />
                          Affecter
                        </>
                      )}
                    </button>
                  </div>
                ));
              })()}
            </div>

            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-500">
                {allAgents?.length || 0} agent(s) au total
              </p>
              <button
                onClick={() => {
                  setShowAgentModal(null);
                  setAgentSearch('');
                  setShowOnlyAvailable(false);
                }}
                className="btn-secondary"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EquipesPage;
